/**
 * Beta K19 (#1317) — Settings → Services & prices, per service: what a client
 * is told about paying after they book.
 *
 * Kate, on HTHD grooming: "pay at pickup isn't really a thing". The default
 * only fits a shop that takes payment at the door, so the owner says how THIS
 * service works. Blank keeps the default; nothing changes for any tenant until
 * someone saves a message.
 *
 * Design Philosophy principles served:
 *  - #1 One glance, one truth: the row says the message the client gets, or
 *    that it is on the default, and who last changed it.
 *  - #2 The next action is obvious: one "Change message", then Save; going
 *    back to the default is a labelled button, not "clear the box and save".
 *  - #5 Never lie about data: the preview is built from the same default
 *    wording the customer app renders, and a shop that takes cards online is
 *    told where this message does and does not appear.
 *  - #7 Built for hands: a plain textarea, the preview under it at phone width.
 *
 * States: read-only (no business-config:write:all), default (Default badge),
 * configured, editing, saving, and save error keeping the typed words. Loading
 * and load error live on the card, which owns the one fetch for all its rows.
 *
 * Counter Test: Ted opens Grooming, types how the desk really takes payment,
 * reads what the client will see, and taps Save. One row, no second screen.
 */
import { useId, useState } from 'react';
import { Alert, Badge, Button } from '@ferro/ui';
import {
  DEFAULT_PAY_AT_PICKUP_DETAIL,
  DEFAULT_PAY_AT_PICKUP_HEADLINE,
  normalizeServicePaymentMessage,
  OWNER_MESSAGE_HEADLINE,
} from '@ferro/core/modules/tenant-settings/service-payment-message.js';
import {
  adminServicePaymentMessagesApi,
  type ServicePaymentMessageEntry,
  type ServicePaymentMessagesView,
} from '../../lib/service-payment-messages-api.js';
import { whenLabel } from './last-change-label.js';

interface Props {
  apiUrl: string;
  serviceName: string;
  entry: ServicePaymentMessageEntry;
  maxLength: number;
  onlinePayments: ServicePaymentMessagesView['onlinePayments'];
  canEdit: boolean;
  onSaved: (entry: ServicePaymentMessageEntry, serviceName: string) => void;
}

export function ServicePaymentMessageField({
  apiUrl,
  serviceName,
  entry,
  maxLength,
  onlinePayments,
  canEdit,
  onSaved,
}: Props): JSX.Element {
  const id = entry.serviceTypeId;
  const inputId = useId();
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const typed = normalizeServicePaymentMessage(text);
  const tooLong = typed.length > maxLength;
  const dirty = (typed === '' ? null : typed) !== entry.message;

  const save = async (message: string | null): Promise<void> => {
    setSaving(true);
    setError(null);
    const res = await adminServicePaymentMessagesApi.update(apiUrl, id, message);
    setSaving(false);
    if (res.error || !res.data) {
      setError(res.error ?? 'Could not save that message. Please try again.');
      return;
    }
    setOpen(false);
    onSaved(res.data.service, serviceName);
  };

  return (
    <div className="ferro-services__pay" data-testid={`service-pay-${id}`}>
      <p className="ferro-services__pay-line">
        <span className="ferro-services__pay-label">After booking</span>{' '}
        {entry.message ? (
          <span data-testid={`service-pay-current-${id}`}>{entry.message}</span>
        ) : (
          <span className="ferro-muted" data-testid={`service-pay-current-${id}`}>
            Clients are told they pay at pickup.{' '}
            <span data-testid={`service-pay-default-${id}`}>
              <Badge variant="platform">Default</Badge>
            </span>
          </span>
        )}
      </p>
      {entry.lastChange ? (
        <p className="ferro-settings__last-change" data-testid={`service-pay-last-change-${id}`}>
          Last changed by <strong>{entry.lastChange.byName}</strong>
          {entry.lastChange.at ? `, ${whenLabel(entry.lastChange.at)}` : ''}
        </p>
      ) : null}

      {canEdit && !open ? (
        <Button
          size="sm"
          variant="ghost"
          className="ferro-services__pay-edit"
          onClick={() => {
            setText(entry.message ?? '');
            setError(null);
            setOpen(true);
          }}
          data-testid={`service-pay-edit-${id}`}
        >
          Change message
        </Button>
      ) : null}

      {canEdit && open ? (
        <form
          className="ferro-form ferro-services__pay-form"
          noValidate
          aria-label={`After-booking message for ${serviceName}`}
          onSubmit={(e) => {
            e.preventDefault();
            if (dirty && !tooLong && !saving) void save(typed === '' ? null : typed);
          }}
          data-testid={`service-pay-form-${id}`}
        >
          <div className="ferro-field">
            <label htmlFor={inputId} className="ferro-field__label">
              How clients pay for {serviceName}
            </label>
            <textarea
              id={inputId}
              className="ferro-field__input ferro-input ferro-services__description"
              rows={3}
              maxLength={maxLength}
              value={text}
              disabled={saving}
              onChange={(e) => setText(e.target.value)}
              aria-describedby={`${inputId}-help`}
              data-testid={`service-pay-input-${id}`}
            />
            <p id={`${inputId}-help`} className="ferro-field__helper">
              Say what your desk really does, in plain words. Leave it blank to use the default. {typed.length} of {maxLength}{' '}
              characters.
            </p>
          </div>

          <div className="ferro-services__pay-preview" data-testid={`service-pay-preview-${id}`}>
            <p className="ferro-services__pay-preview-title">What clients see</p>
            <p>
              <strong>{typed ? OWNER_MESSAGE_HEADLINE : DEFAULT_PAY_AT_PICKUP_HEADLINE}</strong>
            </p>
            <p className="ferro-muted">
              Your {serviceName.toLowerCase()} request is in.{typed ? '' : ` ${DEFAULT_PAY_AT_PICKUP_DETAIL}`}
            </p>
            {typed ? <p data-testid={`service-pay-preview-message-${id}`}>{typed}</p> : null}
          </div>

          <p className="ferro-muted ferro-services__note" data-testid={`service-pay-where-${id}`}>
            {onlinePayments === 'live' || onlinePayments === 'simulation'
              ? 'Clients pay online when they book here, so this appears in their request email only.'
              : 'Clients see this after they book, on My bookings and in their request email.'}
          </p>

          {error ? (
            <div data-testid={`service-pay-error-${id}`}>
              <Alert variant="error">{error}</Alert>
            </div>
          ) : null}

          <div className="ferro-capacity__actions">
            <Button
              type="submit"
              size="sm"
              variant="primary"
              isLoading={saving}
              disabled={!dirty || tooLong || saving}
              data-testid={`service-pay-save-${id}`}
            >
              {saving ? 'Saving…' : 'Save'}
            </Button>
            {entry.message ? (
              <Button
                type="button"
                size="sm"
                variant="ghost"
                disabled={saving}
                onClick={() => void save(null)}
                data-testid={`service-pay-use-default-${id}`}
              >
                Use the default wording
              </Button>
            ) : null}
            <Button type="button" size="sm" variant="ghost" disabled={saving} onClick={() => setOpen(false)} data-testid={`service-pay-cancel-${id}`}>
              Cancel
            </Button>
          </div>
        </form>
      ) : null}
    </div>
  );
}
